// The one dispatcher over the 13 preview components — DecksPage's rail and stage both render
// through here. Content is the slide's own JSON (typed per slide type by the shared contract);
// bound data comes from GET /api/decks/:id/bound, fetched once per deck and passed down.
import type {
  AnhangArbeitsergebnisseContent, AusgangslageContent, BildSplitContent,
  AnhangDividerContent, AnhangRegisterContent, ArbeitsergebnisseTabelleContent,
  ArbeitspaketeGanttContent, AuftragSplitContent, BackCoverContent, CoverContent,
  NaechsteSchritteSplitContent, PreisContent, ReferenzenKartenContent, TeamContent,
  TraceabilityMeshContent, ZielbildTimelineContent,
} from '../../../../shared/deckTypes.js';
import type { DeckSlide, SlideBound } from '../../../api/decks.js';

import AnhangArbeitsergebnissePreview from './AnhangArbeitsergebnissePreview.js';
import AnhangDividerPreview from './AnhangDividerPreview.js';
import AusgangslagePreview from './AusgangslagePreview.js';
import AnhangRegisterPreview from './AnhangRegisterPreview.js';
import ArbeitsergebnisseTabellePreview from './ArbeitsergebnisseTabellePreview.js';
import ArbeitspaketeGanttPreview from './ArbeitspaketeGanttPreview.js';
import AuftragSplitPreview from './AuftragSplitPreview.js';
import BackCoverPreview from './BackCoverPreview.js';
import BildSplitPreview from './BildSplitPreview.js';
import CoverPreview from './CoverPreview.js';
import NaechsteSchritteSplitPreview from './NaechsteSchritteSplitPreview.js';
import PreisPreview from './PreisPreview.js';
import ReferenzenKartenPreview from './ReferenzenKartenPreview.js';
import TeamPreview from './TeamPreview.js';
import TraceabilityMeshPreview from './TraceabilityMeshPreview.js';
import ZielbildTimelinePreview from './ZielbildTimelinePreview.js';

/** How many 1280×720 pages this slide renders as (the two paginated tables span several). */
export function slidePageCount(slide: DeckSlide, bound: SlideBound | undefined): number {
  if (slide.slide_type === 'arbeitsergebnisse_tabelle') return Math.max(1, bound?.tabelle?.pages.length ?? 1);
  if (slide.slide_type === 'anhang_register') return Math.max(1, bound?.anhang?.pages.length ?? 1);
  if (slide.slide_type === 'anhang_arbeitsergebnisse') return Math.max(1, bound?.anhangB?.pages.length ?? 1);
  return 1;
}

export default function SlidePreview({ slide, bound, page = 0, pageNo = null }: {
  slide: DeckSlide;
  bound?: SlideBound;
  /** Which rendered page of a paginated slide to draw (0-based). */
  page?: number;
  /** The printed page number bottom-right; null hides it. */
  pageNo?: number | null;
}) {
  const content = slide.content;
  switch (slide.slide_type) {
    case 'cover':
      return <CoverPreview content={content as CoverContent} bound={bound?.cover} pageNo={pageNo} />;
    case 'ausgangslage':
      return <AusgangslagePreview content={content as AusgangslageContent} pageNo={pageNo} />;
    case 'auftrag_split':
      return <AuftragSplitPreview content={content as AuftragSplitContent} pageNo={pageNo} />;
    case 'zielbild_timeline':
      return <ZielbildTimelinePreview content={content as ZielbildTimelineContent} pageNo={pageNo} />;
    case 'arbeitspakete_gantt':
      return (
        <ArbeitspaketeGanttPreview
          content={content as ArbeitspaketeGanttContent} bound={bound?.gantt} pageNo={pageNo}
        />
      );
    case 'arbeitsergebnisse_tabelle':
      return (
        <ArbeitsergebnisseTabellePreview
          content={content as ArbeitsergebnisseTabelleContent} bound={bound?.tabelle} page={page} pageNo={pageNo}
        />
      );
    case 'traceability_mesh':
      return (
        <TraceabilityMeshPreview
          content={content as TraceabilityMeshContent} bound={bound?.mesh} pageNo={pageNo}
        />
      );
    case 'referenzen_karten':
      return (
        <ReferenzenKartenPreview
          content={content as ReferenzenKartenContent} bound={bound?.referenzen} pageNo={pageNo}
        />
      );
    case 'team':
      return <TeamPreview content={content as TeamContent} bound={bound?.team} pageNo={pageNo} />;
    case 'preis':
      return <PreisPreview content={content as PreisContent} pageNo={pageNo} />;
    case 'naechste_schritte_split':
      return <NaechsteSchritteSplitPreview content={content as NaechsteSchritteSplitContent} pageNo={pageNo} />;
    case 'bild_split':
      return <BildSplitPreview content={content as BildSplitContent} pageNo={pageNo} />;
    case 'anhang_divider':
      return <AnhangDividerPreview content={content as AnhangDividerContent} pageNo={pageNo} />;
    case 'anhang_register':
      return (
        <AnhangRegisterPreview
          content={content as AnhangRegisterContent} bound={bound?.anhang} page={page} pageNo={pageNo}
        />
      );
    case 'anhang_arbeitsergebnisse':
      return (
        <AnhangArbeitsergebnissePreview
          content={content as AnhangArbeitsergebnisseContent} bound={bound?.anhangB} page={page} pageNo={pageNo}
        />
      );
    case 'back_cover':
      return <BackCoverPreview content={content as BackCoverContent} pageNo={pageNo} />;
  }
}
